import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { PAGE_SIZE } from '../indicacao.utils';

export interface DataGridColumn<T> {
	id: string;
	label: string;
	align?: 'left' | 'right';
	render: (row: T) => ReactNode;
}

interface DataGridViewProps<T> {
	columns: DataGridColumn<T>[];
	rows: T[];
	getRowId: (row: T) => string | number;
	empty: ReactNode;
	pageSize?: number;
	renderMobileCard?: (row: T) => ReactNode;
}

export function DataGridView<T>({
	columns,
	rows,
	getRowId,
	empty,
	pageSize = PAGE_SIZE,
	renderMobileCard,
}: DataGridViewProps<T>) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [page, setPage] = useState(0);
	useEffect(() => {
		setPage(0);
	}, [rows.length, pageSize]);
	const precisaPaginacao = rows.length > pageSize;
	const paginaAtual = precisaPaginacao ? rows.slice(page * pageSize, page * pageSize + pageSize) : rows;

	if (rows.length === 0) {
		return <>{empty}</>;
	}

	if (isMobile && renderMobileCard) {
		return (
			<Box>
				<Box display="flex" flexDirection="column" gap={1.25}>
					{paginaAtual.map((row) => (
						<Box key={getRowId(row)}>{renderMobileCard(row)}</Box>
					))}
				</Box>
				{precisaPaginacao && (
					<TablePagination
						component="div"
						count={rows.length}
						page={page}
						onPageChange={(_, next) => setPage(next)}
						rowsPerPage={pageSize}
						rowsPerPageOptions={[pageSize]}
						labelRowsPerPage=""
						labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
					/>
				)}
			</Box>
		);
	}

	return (
		<Box>
			<TableContainer
				component={Paper}
				elevation={0}
				sx={{
					border: '1px solid #f1f5f9',
					borderRadius: 2.5,
					maxHeight: 480,
				}}
			>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow
							sx={{
								'& th': {
									fontWeight: 700,
									color: 'rgb(100, 116, 139)',
									fontSize: '0.72rem',
									letterSpacing: '0.04em',
									textTransform: 'uppercase',
									whiteSpace: 'nowrap',
									bgcolor: '#f8fafc',
									borderBottom: '1px solid #f1f5f9',
								},
							}}
						>
							{columns.map((column) => (
								<TableCell key={column.id} align={column.align ?? 'left'}>
									{column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{paginaAtual.map((row) => (
							<TableRow
								key={getRowId(row)}
								hover
								sx={{
									'& td': { borderBottom: '1px solid #f8fafc', py: 1.25 },
								}}
							>
								{columns.map((column) => (
									<TableCell key={column.id} align={column.align ?? 'left'}>
										{column.render(row)}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			{precisaPaginacao && (
				<TablePagination
					component="div"
					count={rows.length}
					page={page}
					onPageChange={(_, next) => setPage(next)}
					rowsPerPage={pageSize}
					rowsPerPageOptions={[pageSize]}
					labelRowsPerPage=""
					labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
				/>
			)}
		</Box>
	);
}
